'use client'

/**
 * UX review F28 (Matt approved 2026-10-15): a listing runs ~12,000px, so a
 * sticky row of section links lets a buyer jump to what they came for.
 * It reads the page's own h2s after mount (no second source of truth),
 * gives a matched heading an id when it has none, and shows only the
 * sections this listing actually has. Matt 2026-10-25: the row marks the
 * section you are in (aria-current) and keeps that tab in view on phones.
 */
import { useEffect, useRef, useState } from 'react'

const WANTED: Array<{ label: string; match: RegExp; id: string }> = [
  { label: 'Overview', match: /^(at a glance|quick facts)$/i, id: 'ld-overview' },
  { label: 'Facts', match: /^facts$/i, id: 'ld-facts' },
  { label: 'About', match: /^about this (home|property)/i, id: 'ld-about' },
  { label: 'Payment', match: /^payment/i, id: 'ld-payment' },
  { label: 'Schools', match: /^schools/i, id: 'ld-schools' },
  { label: 'History', match: /history/i, id: 'ld-history' },
  { label: 'Documents', match: /cc&rs|governing documents/i, id: 'ld-documents' },
]

type Item = { label: string; id: string }

/** The section whose heading sits lowest while still at or above the bar's bottom edge. */
export function activeSectionId(tops: Array<{ id: string; top: number }>, line: number): string | null {
  let best: { id: string; top: number } | null = null
  for (const t of tops) {
    if (t.top <= line && (!best || t.top > best.top)) best = t
  }
  return best?.id ?? null
}

export function ListingSectionNav() {
  const [items, setItems] = useState<Item[]>([])
  const [active, setActive] = useState<string | null>(null)
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const heads = [...document.querySelectorAll<HTMLHeadingElement>('main h2')]
    const found: Item[] = []
    for (const want of WANTED) {
      const h = heads.find((el) => want.match.test((el.textContent ?? '').trim()))
      if (!h) continue
      if (!h.id) h.id = want.id
      found.push({ label: want.label, id: h.id })
    }
    setItems(found.length >= 3 ? found : [])
  }, [])

  useEffect(() => {
    if (items.length === 0) return
    let frame = 0
    const update = () => {
      frame = 0
      const line = (navRef.current?.getBoundingClientRect().bottom ?? 0) + 24
      const tops = items.flatMap((item) => {
        const h = document.getElementById(item.id)
        return h ? [{ id: item.id, top: h.getBoundingClientRect().top }] : []
      })
      setActive(activeSectionId(tops, line))
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [items])

  // The row scrolls sideways on phones; keep the active tab centred in it.
  useEffect(() => {
    if (!active) return
    const link = navRef.current?.querySelector<HTMLElement>(`a[href="#${active}"]`)
    const row = link?.closest('ul')
    if (!link || !row || row.scrollWidth <= row.clientWidth) return
    const left = link.offsetLeft - row.offsetLeft - (row.clientWidth - link.offsetWidth) / 2
    row.scrollTo({ left, behavior: 'smooth' })
  }, [active])

  if (items.length === 0) return null
  return (
    <nav ref={navRef} className="listing-section-nav" aria-label="Sections on this page">
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            <a href={`#${item.id}`} aria-current={item.id === active ? 'location' : undefined}>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
